import { api, createStorage } from '@/utils/api';
//import { generateUniqueColorFromIdentifierHash } from '@/utils/colors';
import _ from 'lodash';

import { Drivers } from '@ionic/storage';
import * as CordovaSQLiteDriver from 'localforage-cordovasqlitedriver';
//memoryDriver is Fallback option to save to RAM in case the memory in other drivers is not enough. Gets deleted on app restart or when browser needs memory!
import memoryDriver from 'localforage-memoryStorageDriver';

import { STATUS_METADATA_SHORT_KEY_MAPPING } from '@/utils/animals';

const MODULE_VERSION = 5; //UPDATE VERSION WITH EVERY BREAKING CHANGE OR ATTRIBUTE REMOVAL

const PERSONAL_HORSE_INFO_FETCH_ROUTE = '/personal-horse-infos';
const STATUS_FETCH_ROUTE = '/statuses'

const RECENTS_LIMIT = 10;

const statusIndexStorage = createStorage({
    name: '__status_index',
    driverOrder: [CordovaSQLiteDriver._driver, Drivers.IndexedDB, /* Drivers.LocalStorage, */ memoryDriver._driver] //Skip localStorage, as index grows too big!
  },
  [CordovaSQLiteDriver, memoryDriver]
);

const clearPersonalHorseInfoRoute = function() {
  return api.clearRouteMetadata(PERSONAL_HORSE_INFO_FETCH_ROUTE); //Clear route metadata for horses, to fetch them freshly again!
}

const clearStatusIndex = function() {
  return statusIndexStorage.clear()
  .then(() => api.clearRouteMetadata(STATUS_FETCH_ROUTE)); //Clear route metadata for statuses, to fetch them freshly again!
}

const HORSE_DETAIL_PATHS = ['name', 'unique_identifier', 'postcode_stable', 'additional', 'personal_data', 'personal_data_vet'];

const STATUS_DETAIL_PATHS = ['timestamp', 'type', 'details'];

const getDefaultState = () => {
  return {
    version: undefined,
    horses: {},
    personalInfos: {},
    selectedHorse: null,
    recentAnimals: [],
    groupByStatus: false,
    mostRecentStatusUpdate: null,
    statusMetadataIndex: {},
    //Save boolean when loads have finished to capture when the first meaningful load happened
    finishedHorseLoad: false,
    finishedStatusLoad: false
  }
};

export default {
  namespaced: true,

  state: getDefaultState,
  getters: {
    getHorses (state) {
      return state.horses;
    },
    getHorseInfos (state) {
      return state.personalInfos;
    },
    getHorseToPersonalInfoMapping (state, getters) {
      let horses = {};

      _.forEach(getters['getHorseInfos'], (horseInfo, horseInfoId) => {
        if (horseInfo.horses != null) {
          _.forEach(horseInfo.horses, (horse) => {
            if (horse != null && horse.id != null) {
              horses[horse.id] = horseInfoId;
            }
          });
        }
      });

      return horses;
    },
    getStatusesGroupedByHorseInfos (state, getters) {
      let horses = {};

      let mapping = getters['getHorseToPersonalInfoMapping'];

      //Use mostRecentUpdate to always trigger changes!
      if (state.mostRecentStatusUpdate != null) {
        _.forEach(state.statusMetadataIndex, (statusMetadataObjects, type) => {
          _.forEach(statusMetadataObjects, (statusMetadata, statusMetadataId) => {
            if (statusMetadata != null) {
              let horseId = statusMetadata[STATUS_METADATA_SHORT_KEY_MAPPING['horse']];
              if (horseId != null && mapping[horseId] != null) {
                _.setWith(horses, [mapping[horseId], type, statusMetadataId], statusMetadata, Object);
              }
            }
          });
        });
      }

      return horses;
    },
    getHorseInfosWithCurrentStatusAsync (state, getters) {
      let groupedStatuses = getters['getStatusesGroupedByHorseInfos'];

      return function(horseInfos) {
        return Promise.all(_.map(horseInfos, async (horseInfo, horseInfoId) => {
          let status = {};

          let statusesForHorseInfo = groupedStatuses[horseInfoId];

          if (statusesForHorseInfo != null) {
            for (let [category, statusesInCategory] of Object.entries(statusesForHorseInfo)) {
              let currentCategoryStatus = {
                latest: null,
                count: 0
              };

              if (statusesInCategory != null) {
                currentCategoryStatus.count = Object.keys(statusesInCategory).length;

                _.forEach(statusesInCategory, (statusMetadata, statusId) => {
                  let timestamp = _.get(statusMetadata, [STATUS_METADATA_SHORT_KEY_MAPPING['timestamp']]);
                  let updated = _.get(statusMetadata, [STATUS_METADATA_SHORT_KEY_MAPPING['updated_at']]);
                  //Add newer status as latest
                  if (currentCategoryStatus.latest != null) { //Check if it is already set, otherwise always set it for the first time
                    if (timestamp < currentCategoryStatus.latest.timestamp) return;
                    else if (timestamp == currentCategoryStatus.latest.timestamp) { //If they are equal, compare updated_at to get the newer one
                      if (updated < currentCategoryStatus.latest.updated) return;
                    }
                  }

                  currentCategoryStatus.latest = { id: statusId, timestamp: timestamp, updated: updated };
                });
              }

              if (currentCategoryStatus.latest != null) currentCategoryStatus.latest = await statusIndexStorage.get(currentCategoryStatus.latest.id); //Returns Promise!

              status[category] = currentCategoryStatus;
            }
          }

          //Return as key value pairs for promise mapping to work
          return [horseInfoId, { ...horseInfo, status }];
        })).then((horseInfosWithCurrentStatus) => {
          return _.fromPairs(horseInfosWithCurrentStatus);
        });
      }
    },
    getStatusesForHorseWithTypeAsync (state, getters) {
      let groupedStatuses = getters['getStatusesGroupedByHorseInfos'];

      return async function(horseInfoId, type) {
        let statusesForHorseInfo = _.get(groupedStatuses, [horseInfoId, type]);

        if (statusesForHorseInfo == null) return [];

        //Sort by timestamp in reverse order, newest first
        let statusIDs = Object.keys(statusesForHorseInfo)
        statusIDs.sort(function(firstStatusId, secondStatusId){
          //Take the timestamp if it exists, or if an array of multiple is given, try to get the first one or set it to null
          let firstTimestamp = _.get(statusesForHorseInfo, [firstStatusId, STATUS_METADATA_SHORT_KEY_MAPPING['timestamp']]);
          let secondTimestamp = _.get(statusesForHorseInfo, [secondStatusId, STATUS_METADATA_SHORT_KEY_MAPPING['timestamp']]);
          if (firstTimestamp != null && secondTimestamp != null) {
            if (firstTimestamp < secondTimestamp) return 1;
            if (firstTimestamp > secondTimestamp) return -1;
            //If they are equal, compare updated_at
            if (firstTimestamp == secondTimestamp) {
              let firstUpdated = _.get(statusesForHorseInfo, [firstStatusId, STATUS_METADATA_SHORT_KEY_MAPPING['updated_at']]);
              let secondUpdated = _.get(statusesForHorseInfo, [secondStatusId, STATUS_METADATA_SHORT_KEY_MAPPING['updated_at']]);
              if (firstUpdated != null && secondUpdated != null) {
                if (firstUpdated < secondUpdated) return 1;
                if (firstUpdated > secondUpdated) return -1;
              }
            }
          }
          return 0;
        });

        return Promise.all(_.map(statusIDs, (statusId) => statusIndexStorage.get(statusId)));
      }
    },
    getHorseById (state, getters) { //TODO Add currently uploading horses to getter functions too!
      return function(id) {
        if (id in state.horses) {
          let personalHorseId = state.horses[id];
          return { id: personalHorseId, ...getters['getHorseInfos'][personalHorseId]}; //TODO Give back all horse info and if it is currently uploading. If status info too, then it needs to be async like below!
        } else {
          return null;
        }
      }
    },
    getPersonalInfoById (state, getters) { //TODO Add currently uploading horses to getter functions too!
      return function(id) {
        return getters['getHorseInfos'][id]; //TODO Give back all horse info and if it is currently uploading. If status info too, then it needs to be async like below!
      }
    },
    getPersonalInfosWithCurrentStatusAsyncByIds (state, getters) {
      //Gives back all the IDs in the array mapped to personal info with status
      return async function(idArray) {
        return getters['getHorseInfosWithCurrentStatusAsync'](_.pick(getters['getHorseInfos'], idArray));
      }
    },
    getNewestHorseIdForPersonalInfoId (state, getters) {
      return function(id) {
        let personalInfo = getters['getPersonalInfoById'](id);

        if (personalInfo != null && personalInfo.horses != null && Array.isArray(personalInfo.horses)) {
          //Only get horses that are owned by this user
          let ownHorses = _.filter(personalInfo.horses, (horse) => (horse.own === true));
          //Sort to get the newest one (first one, when sorted in descending order)
          ownHorses.sort((a,b) => (new Date(b.created_at) - new Date(a.created_at)));
          //Get the first one and return its id
          return _.get(_.first(ownHorses), 'id');
        }
      }
    },
    getHorsesByOrderAsync (state, getters, rootState, rootGetters) {
      let animalPromise = getters['getHorseInfosWithCurrentStatusAsync'](getters['getHorseInfos']);

      //Create location indexing object
      let locationOptions = {
        //Not assigned animals!!
        null: {
          spots: {
            null: {
              animals: []
            }
          }
        }
      };
      //Add all known locations with their details
      _.forEach(rootGetters['customization/getLocations'], (location, locationIndex) => {
        if (location.descriptor != null && location.spots != null) {
          let spots = {};
          _.forEach(location.spots, (spot, spotIndex) => {
            if (spot.descriptor != null) spots[spot.descriptor] = {
              ...spot,
              order: spotIndex,
              animals: []
            };
          });
          locationOptions[location.descriptor] = {
            ...location,
            order: locationIndex,
            defaultClosed: true, //TODO Make it later an option. Maybe default after certain threshold until set by user!
            spots
          };
        }
      });

      //Create status indexing object
      let caseStatusOptions = {
        //Not assigned animals!!
        null: {
          animals: []
        }
      };
      //Add all known statuses with their details
      _.forEach(rootGetters['customization/getStatuses'], (status, statusIndex) => {
        if (status.descriptor != null) {
          caseStatusOptions[status.descriptor] = {
            ...status,
            order: statusIndex,
            animals: []
          };
        }
      });

      //Return anonymous async function that gets called to retrieve the statuses with a promise
      return (async () => {
        //Copy the prefilled status options from above
        let locations = _.cloneDeep(locationOptions);
        let statuses = _.cloneDeep(caseStatusOptions);
        let animals = await animalPromise;
        //Check for each animal if their location exists, otherwise put it into the unassigned spot. Repeat the same for the status
        for (let [id, animal] of Object.entries(animals)) {
          //---------Location---------
          let currentLocationObject = _.get(animal, ['status', 'location', 'latest'], {});
          let currentLocation = _.get(currentLocationObject, ['details', 'location'], null);
          let currentSpot = _.get(currentLocationObject, ['details', 'spot'], null);

          //Initialize objects if they do not exist yet, or complete them
          _.updateWith(locations, [currentLocation], (locationInstance) => {
            //Set defaults that are undefined still - Takes empty object as starting point if it does not exist yet
            return _.defaults((locationInstance || {}), {
              descriptor: currentLocation,
              order: (currentLocation != null) ? 0 : undefined
            });
          }, Object);
          _.updateWith(locations, [currentLocation, 'spots', currentSpot], (spotInstance) => {
            //Set defaults that are undefined still - Takes empty object as starting point if it does not exist yet
            return _.defaults((spotInstance || {}), {
              descriptor: currentSpot,
              order: (currentSpot != null) ? 0 : undefined
            });
          }, Object);
          //Add to spot
          _.updateWith(locations, [currentLocation, 'spots', currentSpot, 'animals'], (spotInstance) => {
            let spotArray = spotInstance || []; //Use empty array, if it does not exist yet
            spotArray.push(id);
            return spotArray;
          }, Object);

          //---------Status---------
          let currentStatusObject = _.get(animal, ['status', 'case_status', 'latest'], {});
          let currentStatus = _.get(currentStatusObject, ['details', 'status'], null);

          //Initialize object if it does not exist yet, or complete it
          _.updateWith(statuses, [currentStatus], (statusInstance) => {
            //Set defaults that are undefined still - Takes empty object as starting point if it does not exist yet
            return _.defaults((statusInstance || {}), {
              descriptor: currentStatus,
              order: (currentStatus != null) ? 0 : undefined
            });
          }, Object);
          //Add to status
          _.updateWith(statuses, [currentStatus, 'animals'], (statusInstance) => {
            let statusArray = statusInstance || []; //Use empty array, if it does not exist yet
            statusArray.push(id);
            return statusArray;
          }, Object);
        }

        return {
          animals,
          locations,
          statuses
        };
      })();
    },
    getSelectedHorse (state) {
      return state.selectedHorse;
    },
    getRecents (state) {
      return state.recentAnimals;
    },
    shouldGroupByStatus (state) {
      return state.groupByStatus;
    },
    allLoadsFinished (state) {
      return (state.finishedHorseLoad && state.finishedStatusLoad);
    }
  },
  mutations: {
    addHorseInfos (state, newInfos) {
      let newPersonalInfos = {};
      let newHorses = {};

      for (let newInfo of newInfos) {
        newPersonalInfos[newInfo.id] = _.omit(newInfo, 'id');
        if (newInfo.horses != null) {
          for (let horse of newInfo.horses) {
            if (horse.id != null) newHorses[horse.id] = newInfo.id;
          }
        }
      }
      
      //Add all the new objects to the index once to trigger just one recompute
      _.assign(state.personalInfos, newPersonalInfos);
      _.assign(state.horses, newHorses);
    },
    finishHorseLoad (state) {
      if (!(state.finishedHorseLoad)) state.finishedHorseLoad = true;
    },
    finishStatusLoad (state) {
      if (!(state.finishedStatusLoad)) state.finishedStatusLoad = true;
    },
    setSelectedHorse (state, newHorse) {
      state.selectedHorse = newHorse;
    },
    addToRecents (state, animal) {
      if (animal == null || animal == 'null') return;
      let newRecents = _.filter(state.recentAnimals, (currentAnimal) => {
        return (currentAnimal != animal);
      }); //Remove any previous occurrences of this animal in the list
      state.recentAnimals = _.take(_.concat([animal], newRecents), RECENTS_LIMIT); //Add it at the top and only take elements up to the limit
    },
    setGroupByStatusState (state, newState) {
      state.groupByStatus = newState;
    },
    addStatusIndexMetadata (state, newStatusEntries) {
      let mostRecentUpdate = state.mostRecentStatusUpdate;
      //Save temporarily all new metadata and add all of them at once in the end!
      let newStatusMetadataForIndex = {};
      //Go through each status entry and add the metadata to the object above grouped by the type
      for (let newStatusEntry of newStatusEntries) {
        //Create a copy for modifications
        let statusEntry = {...newStatusEntry};
        //Update metadata information about the index in different storage, if changed or not set yet
        if (statusEntry['updated_at'] != null && (mostRecentUpdate == null || ((new Date(statusEntry['updated_at'])) > (new Date(mostRecentUpdate)))) ) {
          mostRecentUpdate = statusEntry['updated_at'];
        }

        //TODO When deleted_at, remove from index.

        //Pick only the metadata that should be saved. Exclude invalid metadata to reduce space usage.
        let newMetadata = _.pickBy(statusEntry, (value, key) => {
          //If it is a valid metadata key, check if valid
          if (key in STATUS_METADATA_SHORT_KEY_MAPPING) {
            //If it is not null or not an empty array (Either not array or more that one element), include it
            if (value != null && ((!Array.isArray(value)) || value.length >= 1)) return true;
          }
          return false; //Otherwise it is a not used metadata key or it is empty, so don't include it to save space
        });
        //Set in the metadata index with all necessary information. Convert keys to short keys, or keep long key, as a fallback.
        _.setWith(newStatusMetadataForIndex, [statusEntry['type'], statusEntry['id']], _.mapKeys(newMetadata, (value, key) => STATUS_METADATA_SHORT_KEY_MAPPING[key] || key), Object);
      }

      //Update the most recent update timestamp if it changed
      if (mostRecentUpdate != null && (state.mostRecentStatusUpdate == null || ((new Date(mostRecentUpdate)) > (new Date(state.mostRecentStatusUpdate)))) ) {
        state.mostRecentStatusUpdate = mostRecentUpdate;
      }
      //Add all the new objects to the metadata index once, for each category it needs to be assigned
      _.assignWith(state.statusMetadataIndex, newStatusMetadataForIndex, (statusCategoryValues, newCategoryValues) => _.assign((statusCategoryValues || {}), newCategoryValues));
    },
    checkVersion (state) { //Checks the version of this module and sets the state to default, if version is different
      //Used to reset state on breaking changes and to remove attributes that are no longer needed
      if (state.version !== MODULE_VERSION) {
        let defaultState = getDefaultState();
        Object.assign(state, defaultState); //Use setters to not impact any reactive functionality
        //Search for unused attributes and set them undefined, to remove them from the persisted state
        for (let key of Object.keys(state)){
          if (!(key in defaultState)){
            state[key] = undefined;
          }
        }
        clearStatusIndex();
        clearPersonalHorseInfoRoute();
        state.version = MODULE_VERSION;
      }
    },
    clearPersonalData (state) {
      let defaultState = {};
      Object.assign(defaultState, getDefaultState());
      state.horses = defaultState.horses;
      state.selectedHorse = defaultState.selectedHorse;
      state.personalInfos = defaultState.personalInfos;
      state.mostRecentStatusUpdate = defaultState.mostRecentStatusUpdate;
      state.statusMetadataIndex = defaultState.statusMetadataIndex;
      state.finishedHorseLoad = defaultState.finishedHorseLoad;
      state.finishedStatusLoad = defaultState.finishedStatusLoad;
      state.recentAnimals = defaultState.recentAnimals,
      state.groupByStatus = defaultState.groupByStatus,
      clearStatusIndex();
      clearPersonalHorseInfoRoute();
    }
  },
  actions: {
    fetchHorses (context) {
      return new Promise((resolve, reject) => {
        api
        .get(PERSONAL_HORSE_INFO_FETCH_ROUTE, { fetchNewerForAnyPath: ['updated_at'] })
        .then(response => { //Add to object with id as index
          if (response.data != null) {
            context.commit('addHorseInfos', response.data);
          }

          context.commit('finishHorseLoad');
          
          resolve();
        })
        .catch(error => {
          reject(error);
        });
      });
    },
    createNewHorse (context, { animalInfo, files }) {
      let user = context.rootGetters['auth/getUser'];
      let uploadPomise = api.post('/personal-horse-infos', animalInfo, {
        files,
        user: _.get(user, 'id'),
        statusDetailPaths: HORSE_DETAIL_PATHS
      }); 
      //TODO In the future add this horse to the state, no need to load again! Keep files in mind!
      uploadPomise.then((uploadStatus) => uploadStatus.completionPromise).then(() => context.dispatch('fetchHorses'));
      return uploadPomise;
    },
    updateHorse (context, { id, animalInfo, files }) {
      let user = context.rootGetters['auth/getUser'];
      let uploadPomise = api.put(`/personal-horse-infos/${id}`, animalInfo, {
        files,
        user: _.get(user, 'id'),
        statusDetailPaths: HORSE_DETAIL_PATHS
      });
      //TODO In the future add this horse to the state, no need to load again! Keep files in mind!
      uploadPomise.then((uploadStatus) => uploadStatus.completionPromise).then(() => context.dispatch('fetchHorses'));
      return uploadPomise;
    },
    updateSelectedHorse (context, newHorse) {
      context.commit('setSelectedHorse', newHorse);
      context.commit('addToRecents', newHorse);
    },
    fetchStatuses (context) {      
      return new Promise((resolve, reject) => {
        api
        .get(STATUS_FETCH_ROUTE, { fetchNewerForAnyPath: ['updated_at'] })
        .then(async response => {
          for (let newEntry of response.data){
            let statusEntry = {...newEntry};

            await statusIndexStorage.set(statusEntry.id, statusEntry);
          }

          context.commit('addStatusIndexMetadata', response.data);

          context.commit('finishStatusLoad');
          
          resolve();
        })
        .catch(error => {
          reject(error);
        });
      });
    },
    fetchStatus (context, status) {
      let user = context.rootGetters['auth/getUser'];
      return api
        .get(`/statuses/${status}`, {
          user: _.get(user, 'id'),
        }).then(async response => response.data);
    },
    addStatus (context, { status, files }) {
      let user = context.rootGetters['auth/getUser'];
      let uploadPomise = api.post('/statuses', status, {
        files,
        user: _.get(user, 'id'),
        statusDetailPaths: STATUS_DETAIL_PATHS
      }); 
      //TODO In the future add this status to the state, no need to load again! Keep files in mind!
      uploadPomise.then((uploadStatus) => uploadStatus.completionPromise).then(() => context.dispatch('fetchStatuses'));
      return uploadPomise;
    },
    updateStatus (context, { id, status, files }) {
      let user = context.rootGetters['auth/getUser'];
      let uploadPomise = api.put(`/statuses/${id}`, status, {
        files,
        user: _.get(user, 'id'),
        statusDetailPaths: STATUS_DETAIL_PATHS
      });
      //TODO In the future add this status to the state, no need to load again! Keep files in mind!
      uploadPomise.then((uploadStatus) => uploadStatus.completionPromise).then(() => context.dispatch('fetchStatuses'));
      return uploadPomise;
    },
  }
}